import ApiServer from "../ApiServer.ts";
import * as FwDatas from "../FwDatas.ts";
import {isSameDay} from "../lib/DateUtils.ts";
import * as i18n from "../i18n.ts";
import * as decoders from "../server/decoders_connected.ts";
import { IIMMessage, IIMParticipant, IIMThread, SimplePaginatedCollection, SimplePaginationRequest } from "../server/fizapilib.auto.ts";

/** participantForImg() splices in a fake participant carrying the "+n" badge when there are too many */
type ParticipantAvatar = IIMParticipant | {avatar: string};

// Augmentation of the generated beans with the properties and methods added dynamically below
declare module "../server/fizapilib.auto.ts" {
  interface IIMThread {
    /** the last messages of the thread, loaded page by page */
    messages?: SimplePaginatedCollection<IIMMessage>;
    lastmessage?: IIMMessage;
    participantLine(): string;
    participantKey(): string;
    participantForImgLength(): number;
    participantForImg(): ParticipantAvatar[];
  }
  interface IIMParticipant {
    avatar(): string;
  }
  interface IIMMessage {
    /** the medias grouped by kind, see mapMedia() */
    gallery?: decoders.Gallery;
    /** the text without its giphy links, see decodeGiphy() */
    textraw?: string;
    giphy?: string[];
    /** the participant who sent the message */
    participant?: IIMParticipant;
    meAsParticipant?: IIMParticipant;
    isUnread?: boolean;
    /** firstname of the sender, copied from the participant */
    accountFirstname?: string;
    /** what the thread list displays as a summary */
    preview?: string;
    /** grouping of the consecutive messages of one author, computed by prepareMessageList() */
    hideAuthor?: boolean;
    hideTime?: boolean;
    hideDate?: boolean;
    thread(): IIMThread;
    author(): string;
    isMe(): boolean;
  }
}

var threadByIds: Record<string, IIMThread> = {},threadIdByParticipantKey: Record<string, string> = {};
Object.assign(IIMThread.prototype,{
  participantLine :function(this: IIMThread): string {
    var
      i: number,
      builder = "";

    for(i=1;i<this.participants.length;i++){
      builder += this.participants[i].accountFirstname+', ';
    }
    builder= builder.substring(0,builder.length-2);
    return builder;
  },
  participantKey:function(this: IIMThread): string {
    return this.participants.slice(1).map(function(o){return o.accountId;}).sort().join(',');
  },
  participantForImgLength :function(this: IIMThread): number {
    return Math.min(4,this.participants.length-1);
  },
  participantForImg :function(this: IIMThread): ParticipantAvatar[] {
    var extraCount: number,toDisplay: ParticipantAvatar[] = this.participants.filter(function(p,idx){
      return !p.isLogguedAccount;
    });
    if(toDisplay.length>4){
      extraCount = toDisplay.length-3;
      toDisplay.splice(3,extraCount,{avatar:"<span class='"+(extraCount>9?'fw-participant-extra-small':'')+"'>+"+extraCount+"</span>"});
    }
    return toDisplay;
  },
  onDecode(this: IIMThread): void {
    threadIdByParticipantKey[this.participantKey()] = this.metaId;
  }
});
Object.assign(IIMParticipant.prototype,{
  avatar:function(this: IIMParticipant): string {
    return "<img src='"+this.thumb()+"' title='"+this.accountFirstname+"'>";
  }
});
Object.assign(IIMMessage.prototype,{
  onDecode(this: IIMMessage): void {
    decoders.mapMedia(this);
    decoders.decodeGiphy(this);
    var thread = threadByIds[this.threadId],i: number,len: number;
    //a thread just created by the send message will not be in cache
    if(!thread){
      return;
    }
    for(i=0,len=thread.participants.length;i<len;i++){
      var p = thread.participants[i];
      if(p.accountId===this.fromId){
        this.participant = p;
      }
      if(p.isLogguedAccount){
        this.meAsParticipant = p;
      }
    }
    this.isUnread = this.creationDate>this.meAsParticipant.lastReadMessageDate;
    this.accountFirstname = this.participant.accountFirstname;
    this.preview = this.text.match(/https?:\/\/[^\s]*gif/g)?(this.isMe()?i18n.get("im.gifsent"):i18n.getMustache("im.gifreceived",this)):this.text;
  },
  thread:function(this: IIMMessage): IIMThread {
    return threadByIds[this.threadId];
  },
  author:function(this: IIMMessage): string {
    return FwDatas.getProfile(this.fromId).firstName;
  },
  isMe:function(this: IIMMessage): boolean {
    return this.participant.isLogguedAccount;
  }
});
function prepareMessageList(list: IIMMessage[]): void {
  list.sort(function(a,b){
    return a.creationDate.getTime()-b.creationDate.getTime();
  });
  var prevMessage = list[0],i: number;
  for(i=1;i<list.length;i++){
    var message = list[i];
    if(message.fromId===prevMessage.fromId){
      message.hideAuthor = true;
      if(message.creationDate.getTime()-prevMessage.creationDate.getTime()<60000){
        prevMessage.hideTime = true;
      }
    }else{
      message.hideAuthor = false;
    }
    if(isSameDay(message.creationDate,prevMessage.creationDate)){
      message.hideDate = true;
    }

    prevMessage = message;
  }
}

var cachedThread: IIMThread[];
function isEquals(threadList1: IIMThread[], threadList2: IIMThread[]): boolean {
  if(threadList1.length===threadList2.length){
    for(var i=0;i<threadList2.length;i++){
      if(threadList1[i].messageListModifDate.getTime() - threadList2[i].messageListModifDate.getTime() !==0){
        return false;
      }
    }
    return true;
  }
  return false;
}

export async function withallthread(isInit?: boolean): Promise<IIMThread[]> {
  //if isInit and something in cache we do it while checking if there is a change
  if(isInit && cachedThread!==undefined){
    return cachedThread;
  }
  var threads: IIMThread[] = await ApiServer.customimthreadlist()
  //sort thread
  threads.sort(function(a,b){
    return b.sortingDate.getTime()-a.sortingDate.getTime();
  });

  if(cachedThread!==undefined && isEquals(threads,cachedThread)){
    if(isInit){
      return cachedThread;
    }else{
      threads = cachedThread;
    }
  }else{
    cachedThread = threads;
  }

  //store them by id
  threads.forEach(function(obj){
    var oldThread = threadByIds[obj.metaId];
    threadByIds[obj.metaId] = obj;
    if(oldThread){
      if(oldThread.messageListModifDate.getTime()===obj.messageListModifDate.getTime()){
        //keep messages if unchanged
        obj.messages = oldThread.messages;
        obj.lastmessage = oldThread.lastmessage;
      }else{
        //console.log("thread updated")
      }
    }
  });

  var idsToUpdate = threads.filter(thread=>thread.messages===undefined).map(function(o){return o.metaId;});
  if(idsToUpdate.length){
    //load last message of each
    var list = await ApiServer.immessagelistgrp(idsToUpdate,1)
    for(var i=0;i<list.length;i++){
      var thread = threadByIds[idsToUpdate[i]];
      thread.messages=list[i];
      thread.lastmessage = thread.messages.datas[0];
    }
  }
  return threads;
}
export async function withfullthread(threadId: string, refresh?: boolean): Promise<IIMThread> {
  var
    thread = threadByIds[threadId],
    messages = thread.messages;

  if(refresh || (messages.nextPageId!=null &&  messages.datas.length<=1)){
    messages = await ApiServer.immessagelist2(threadId, new SimplePaginationRequest({count:50} as any));
    thread.lastmessage = messages.datas[0];
    thread.messages = messages;
    prepareMessageList(messages.datas);
  }
  return thread;

}
export async function withOlderMessages(threadId: string, nextPageId: string): Promise<{messages: SimplePaginatedCollection<IIMMessage>}> {
  const messages: SimplePaginatedCollection<IIMMessage> = await ApiServer.immessagelist2(threadId,new SimplePaginationRequest({count:50,pageId:nextPageId} as any))
  prepareMessageList(messages.datas);
  //give a fake thread with only asked messages
  return {messages};

}
export function withparticipant(participantKey: string | string[]): string | undefined {
  if(typeof participantKey === 'object'){
    participantKey = participantKey.sort().join(',');
  }
  return (threadIdByParticipantKey[participantKey]);
}
